import React, { useEffect, useRef } from "react";
import {
  Animated,
  LayoutRectangle,
  StyleProp,
  View,
  ViewProps,
} from "react-native";
import { useLayoutProvider } from "../../../modules/point-of-sale/utils/layout";
import {
  DragDropContext,
  DraggingComponent,
  DropZone,
  Point,
} from "../../../utilities/drag-drop";
import {
  PageType,
  useAnimator,
  useLayoutEditController,
} from "../../../utilities/layout";
import { DragModal } from "./components";
import { DEFAULT_VALUE_XY } from "./constants";
import { useDrag } from "./use-drag";
import { useDrop } from "./use-drop";
import { useZones } from "./use-zones";
import { makeAnimationGetters } from "./utils";

export type DragContainerProps = {
  pages: PageType[];
  style?: StyleProp<ViewProps>;
  children?: React.ReactNode;
  onDragStart?: (
    draggingComponent: DraggingComponent,
    hitZones?: DropZone[]
  ) => void;
  onDragEnd?: () => void;
};

export function DragContainer({
  pages,
  style,
  children,
  onDragStart,
  onDragEnd,
}: DragContainerProps) {
  const performLayoutAction = useLayoutEditController();

  const pointerRef = useRef<Animated.ValueXY>(new Animated.ValueXY());
  const draggingComponentRef = useRef<any | null>(null);
  const containerLayoutRef = useRef<LayoutRectangle | null>(null);
  const dropZonesRef = useRef<Map<string, DropZone>>(new Map());
  const offsetRef = useRef<Point | null>(null);
  const enteredZoneRef = useRef<string | null>(null);

  const [showPortal, setShowPortal] = React.useState(false);

  const { layoutDimensions, isEditing } = useLayoutProvider();

  useEffect(() => {
    if (!isEditing) {
      draggingComponentRef.current = null;
      enteredZoneRef.current = null;
      offsetRef.current = null;
      dropZonesRef.current = new Map();
    }
  }, [isEditing]);

  const itemsPerPage = pages[0]?.items.length;

  const animationController = useAnimator({
    ...makeAnimationGetters(pages, dropZonesRef, draggingComponentRef),
    itemsPerPage,
    numColumns: layoutDimensions?.columns ?? 0,
  });

  const animationRegistryRef = useRef(animationController.animationRegistry);

  const zoneControllerProps = {
    pointerRef,
    dropZonesRef,
    draggingComponentRef,
    enteredZoneRef,
  };

  const dragController = useDrag({
    draggingComponentRef,
    animationRegistryRef,
    offsetRef,
    onDragStart: handleDragStart,
  });

  const dropController = useDrop({
    ...zoneControllerProps,
    offsetRef,
    animationController,
    onDragEnd: handleDragEnd,
  });

  const zoneController = useZones(zoneControllerProps);

  function handleDragStart(draggingComponent: DraggingComponent) {
    setShowPortal(true);
    zoneController.updateZone({
      id: `${draggingComponent.data.position}`,
      ref: draggingComponent.ref,
      ...draggingComponent.startPosition,
    });
    onDragStart?.(draggingComponent);
  }

  function handleDragEnd() {
    setShowPortal(false);
    onDragEnd?.();

    draggingComponentRef.current = null;
    enteredZoneRef.current = null;
    offsetRef.current = null;
    dropZonesRef.current = new Map();
  }

  function getUnregisteredNodes() {
    const allNodes = pages.flatMap((page) => page.items);

    const unregisteredNodes = allNodes
      .filter((node) => {
        const isRegistered = animationController.animationRegistry.has(node.id);
        return !isRegistered;
      })
      .map((node) => node.id);

    return unregisteredNodes;
  }

  function registerNewLayoutNodes() {
    getUnregisteredNodes().forEach((id) => {
      animationController.animationRegistry.register(
        id,
        new Animated.ValueXY(DEFAULT_VALUE_XY, { useNativeDriver: true })
      );
    });
  }

  useEffect(registerNewLayoutNodes, [pages]);

  const controllers = {
    dragController,
    dropController,
    zoneController,
    animationController,
  };

  return (
    <View
      style={[{ flex: 1, touchAction: "none" }, style as any]}
      onLayout={(e) => (containerLayoutRef.current = e.nativeEvent.layout)}
    >
      <DragDropContext.Provider
        value={{
          controllers,
          container: containerLayoutRef.current,
          draggingComponentRef,
          pointerRef,
          offsetRef,
          performLayoutAction,
        }}
      >
        {children}
        {showPortal ? <DragModal /> : null}
      </DragDropContext.Provider>
    </View>
  );
}
